/**
 * Square icon button — used in top bar, sidebar collapse, chat composer, etc.
 *
 * Always carries an aria-label (PRD §9.9). Visual variants:
 *   - default: ghost on bg, accent on hover
 *   - solid: filled accent
 */
import type { ComponentPropsWithoutRef } from 'react';

import { cn } from '../../lib/cn';

interface Props extends ComponentPropsWithoutRef<'button'> {
  'aria-label': string;
  variant?: 'default' | 'solid';
  size?: 'sm' | 'md';
}

export function IconButton({
  variant = 'default',
  size = 'md',
  className,
  children,
  ...rest
}: Props): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded-tight transition',
        size === 'sm' ? 'size-7' : 'size-8',
        variant === 'solid'
          ? 'bg-accent text-accent-fg hover:opacity-90'
          : 'text-ink-muted hover:bg-paper-soft hover:text-ink',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      {children}
    </button>
  );
}
